import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";

const SCENARIOS = [
  {
    tag: "ROOT-CAUSE ANALYSIS",
    query: "Why did Line 3 stop yesterday?",
    lines: [
      "pulling context … knowledge graph: 214 nodes",
      "analysing downtime events … 3 matched",
      "root cause: infeed sensor fault · confidence 0.94",
    ],
    trace: "trace → historian:P3-114 · alarm:A-221 · SOP-7.3",
  },
  {
    tag: "DASHBOARD",
    query: "Build an OEE dashboard for the press shop.",
    lines: [
      "connecting … historian:P3-* · MES shift calendar",
      "computing availability, performance, quality … done",
      "dashboard ready · 4 widgets · refreshing live",
    ],
    trace: "trace → availability 92.1% · performance 89.4% · quality 98.2%",
  },
  {
    tag: "RECOMMENDATION",
    query: "How do we cut energy use on Kiln 2?",
    lines: [
      "pulling context … kiln parameters + energy meters",
      "comparing against SOP bounds … 2 deviations found",
      "recommend: lower idle setpoint 40°C · est. -6.8% energy",
    ],
    trace: "trace → meter:K2-EN · sop:KILN-4.1 · trend:90d",
  },
  {
    tag: "ALARM INSIGHT",
    query: "Why does alarm A-221 keep firing?",
    lines: [
      "pulling context … alarm history: 47 events / 30d",
      "correlating with process state … 89% follow infeed jams",
      "insight: sensor drift · recalibrate P3-114",
    ],
    trace: "trace → alarm:A-221 · historian:P3-114 · cmms:WO-8831",
  },
];

const CHAR_MS = 45;
const LINE_MS = 1000;

export const HeroConsole = () => {
  const [scenario, setScenario] = useState(0);
  const [typed, setTyped] = useState("");
  const [lines, setLines] = useState(0);
  const [showTrace, setShowTrace] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timers = [];
    const sc = SCENARIOS[scenario];
    setTyped("");
    setLines(0);
    setShowTrace(false);

    for (let i = 1; i <= sc.query.length; i++) {
      timers.push(setTimeout(() => !cancelled && setTyped(sc.query.slice(0, i)), i * CHAR_MS));
    }
    const base = sc.query.length * CHAR_MS + 600;
    sc.lines.forEach((_, idx) => {
      timers.push(setTimeout(() => !cancelled && setLines(idx + 1), base + idx * LINE_MS));
    });
    timers.push(setTimeout(() => !cancelled && setShowTrace(true), base + sc.lines.length * LINE_MS + 700));
    timers.push(
      setTimeout(
        () => !cancelled && setScenario((s) => (s + 1) % SCENARIOS.length),
        base + sc.lines.length * LINE_MS + 4200
      )
    );
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [scenario]);

  const sc = SCENARIOS[scenario];

  return (
    <motion.div
      animate={{ y: [0, -10, 0] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      className="relative"
      data-testid="hero-console"
    >
      <div className="absolute -inset-6 bg-teal-500/15 blur-3xl rounded-full -z-10" />
      <div className="border border-white/10 bg-[#0b0f0e]/90 backdrop-blur-xl shadow-2xl shadow-black/40">
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-white/10">
          <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
          <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
          <span className="w-2.5 h-2.5 rounded-full bg-teal-400/70" />
          <span className="ml-3 font-mono text-[11px] text-white/40">streamline360 — agent console</span>
          <AnimatePresence mode="wait">
            <motion.span
              key={sc.tag}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3 }}
              className="ml-auto font-mono text-[10px] tracking-[0.15em] px-2.5 py-1 border border-teal-400/40 text-teal-300"
              data-testid="console-scenario-tag"
            >
              {sc.tag}
            </motion.span>
          </AnimatePresence>
        </div>
        <div className="p-5 lg:p-6 font-mono text-[13px] leading-relaxed min-h-[236px]">
          <p className="text-white/90">
            <span className="text-teal-400 mr-2">›</span>
            {typed}
            <span className="inline-block w-2 h-4 bg-teal-400/80 align-middle ml-0.5 animate-pulse" />
          </p>
          <div className="mt-4 space-y-2.5">
            <AnimatePresence>
              {sc.lines.slice(0, lines).map((line, i) => (
                <motion.p
                  key={`${scenario}-${line}`}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4 }}
                  className={i === sc.lines.length - 1 ? "text-green-400" : "text-white/50"}
                >
                  <Check size={12} className={`inline mr-2 ${i === sc.lines.length - 1 ? "text-green-400" : "text-teal-400/70"}`} />
                  {line}
                </motion.p>
              ))}
            </AnimatePresence>
          </div>
          {showTrace && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 text-[11px] text-teal-400/80"
            >
              {sc.trace}
            </motion.p>
          )}
        </div>
        <div className="flex items-center gap-1.5 px-5 pb-4" data-testid="console-scenario-dots">
          {SCENARIOS.map((s, i) => (
            <button
              key={s.tag}
              onClick={() => setScenario(i)}
              aria-label={s.tag}
              data-testid={`console-dot-${i}`}
              className={`h-1 rounded-full transition-[width,background-color] duration-500 ${
                i === scenario ? "w-6 bg-teal-400" : "w-2 bg-white/15 hover:bg-white/30"
              }`}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};
